/**
 * useBookingActions Hook
 *
 * Provides actions for managing bookings via Cloud Functions and Firestore.
 */

import { useState, useCallback } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../../services/firebase';

export interface DeclineData {
  bookingId: string;
  reason: string;
  message?: string;
}

export interface CancelData {
  bookingId: string;
  reason: string;
}

const respondCallable = httpsCallable<
  { bookingId: string; action: string; reason?: string; message?: string },
  { success: boolean; action: string }
>(functions, 'respondToBooking');

export const useBookingActions = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const acceptBooking = useCallback(async (bookingId: string): Promise<boolean> => {
    setIsProcessing(true);
    setError(null);

    try {
      await respondCallable({ bookingId, action: 'accept' });
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to accept booking';
      setError(message);
      return false;
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const declineBooking = useCallback(
    async (data: DeclineData): Promise<boolean> => {
      setIsProcessing(true);
      setError(null);

      try {
        await respondCallable({
          bookingId: data.bookingId,
          action: 'decline',
          reason: data.reason,
          message: data.message,
        });
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to decline booking';
        setError(message);
        return false;
      } finally {
        setIsProcessing(false);
      }
    },
    []
  );

  const cancelBooking = useCallback(
    async (data: CancelData): Promise<boolean> => {
      setIsProcessing(true);
      setError(null);

      try {
        await respondCallable({
          bookingId: data.bookingId,
          action: 'cancel',
          reason: data.reason,
        });
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to cancel booking';
        setError(message);
        return false;
      } finally {
        setIsProcessing(false);
      }
    },
    []
  );

  const completeBooking = useCallback(async (bookingId: string): Promise<boolean> => {
    setIsProcessing(true);
    setError(null);

    try {
      const bookingRef = doc(db, 'bookings', bookingId);
      await updateDoc(bookingRef, {
        status: 'completed',
        completedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return true;
    } catch (err) {
      setError('Failed to complete booking');
      return false;
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const checkInGuest = useCallback(async (bookingId: string): Promise<boolean> => {
    setIsProcessing(true);
    setError(null);

    try {
      const bookingRef = doc(db, 'bookings', bookingId);
      await updateDoc(bookingRef, {
        status: 'active',
        checkedInAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return true;
    } catch (err) {
      setError('Failed to check in guest');
      return false;
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const checkOutGuest = useCallback(async (bookingId: string): Promise<boolean> => {
    setIsProcessing(true);
    setError(null);

    try {
      const bookingRef = doc(db, 'bookings', bookingId);
      await updateDoc(bookingRef, {
        status: 'completed',
        checkedOutAt: serverTimestamp(),
        completedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return true;
    } catch (err) {
      setError('Failed to check out guest');
      return false;
    } finally {
      setIsProcessing(false);
    }
  }, []);

  return {
    acceptBooking,
    declineBooking,
    cancelBooking,
    completeBooking,
    checkInGuest,
    checkOutGuest,
    isProcessing,
    error,
  };
};

export default useBookingActions;
